import { useState } from "react";
import { useNavigate, Navigate, Link } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Shield, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { user, login, loading } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  if (loading) return null;
  if (user) return <Navigate to="/" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const u = await login(email.trim().toLowerCase(), password);
      toast.success(`Welcome, ${u.full_name}`);
      nav("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] text-white grain flex items-center justify-center p-4">
      <div className="max-w-md w-full relative z-10">
        <div className="mb-8 flex items-center gap-3">
          <div className="hazard-stripes w-3 h-12 rounded-sm" />
          <div>
            <div className="font-display text-3xl font-black tracking-tight">PARKLOCK</div>
            <div className="text-xs tracking-[0.3em] text-[#94A3B8] uppercase">Field Operations Console</div>
          </div>
        </div>
        <form onSubmit={submit} className="glass rounded-2xl p-6 sm:p-8 space-y-5" data-testid="login-form">
          <div>
            <h1 className="font-display text-2xl font-bold">Sign in</h1>
            <p className="text-sm text-[#94A3B8] mt-1">Access your shift, boots, and dispatch.</p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              type="email"
              autoComplete="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              className="bg-[#0B0F17] border-[#28364F] text-white"
              data-testid="login-email-input"
              placeholder="you@team.com"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              className="bg-[#0B0F17] border-[#28364F] text-white"
              data-testid="login-password-input"
              placeholder="••••••••"
            />
          </div>
          <Button
            type="submit"
            disabled={busy}
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-[#0B0F17] font-bold h-11"
            data-testid="login-submit-btn"
          >
            <KeyRound className="w-4 h-4 mr-2" />
            {busy ? "Signing in..." : "Sign in"}
          </Button>
          <div className="text-center text-sm text-[#94A3B8] pt-2 border-t border-[#28364F] space-y-1">
            <div>
              <Link to="/forgot-password" className="text-amber-400 hover:text-amber-300 font-semibold" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
            <div>
              New team member?{" "}
              <Link to="/signup" className="text-cyan-400 hover:text-cyan-300 font-semibold" data-testid="go-to-signup-link">
                Set up your credentials →
              </Link>
            </div>
          </div>
          <div className="flex items-start gap-2 text-xs text-[#64748B]">
            <Shield className="w-4 h-4 shrink-0 mt-0.5 text-cyan-500" />
            <span>Each user gets an individual login. Enforcers see only their shift; admins have full oversight.</span>
          </div>
        </form>
      </div>
    </div>
  );
}
